import React, { useEffect, useRef } from 'react';
import { SpeakerSimpleHigh, SpeakerSimpleSlash, Bell, BellSlash, MusicNotes, Play, Pause, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useNarration } from '../context/NarrationContext';
import { useSoundEffects } from '../context/SoundEffectsContext';
import { useBackgroundMusic } from '../context/BackgroundMusicContext';
import './NarrationMenu.css';

interface NarrationMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const SPEED_PRESETS = [1, 1.25, 1.5, 2] as const;

const NarrationMenu: React.FC<NarrationMenuProps> = ({ isOpen, onClose }) => {
  const { state, toggleEnabled, pauseAudio, resumeAudio, replaySection, activateAudio, setPlaybackRate } = useNarration();
  const { state: sfxState, toggleEnabled: toggleSfx } = useSoundEffects();
  const { state: musicState, toggleEnabled: toggleMusic } = useBackgroundMusic();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleToggle = () => {
    toggleEnabled();
  };

  const handlePauseResume = () => {
    if (state.status === 'playing') {
      pauseAudio();
    } else if (state.status === 'paused') {
      resumeAudio();
    }
  };

  const isAudioActive = (state.status === 'playing' || state.status === 'paused') && !state.needsUserActivation;

  return (
    <div className="narration-menu" ref={dropdownRef}>
      {/* Enable/Disable Toggle - Always visible at top */}
      <label className="menu-item narration-toggle">
        <span className="menu-icon">{state.enabled ? <SpeakerSimpleHigh size={18} weight="duotone" /> : <SpeakerSimpleSlash size={18} weight="duotone" />}</span>
        <span className="menu-text">Narration</span>
        <input
          type="checkbox"
          checked={state.enabled}
          onChange={handleToggle}
          className="narration-checkbox"
        />
      </label>

      {/* SFX Toggle */}
      <label className="menu-item sfx-toggle">
        <span className="menu-icon">{sfxState.enabled ? <Bell size={18} weight="duotone" /> : <BellSlash size={18} weight="duotone" />}</span>
        <span className="menu-text">Sound Effects</span>
        <input
          type="checkbox"
          checked={sfxState.enabled}
          onChange={toggleSfx}
          className="sfx-checkbox"
        />
      </label>

      {/* Music Toggle */}
      <label className="menu-item music-toggle">
        <span className="menu-icon">{musicState.enabled ? <MusicNotes size={18} weight="duotone" /> : <SpeakerSimpleSlash size={18} weight="duotone" />}</span>
        <span className="menu-text">Music</span>
        <input
          type="checkbox"
          checked={musicState.enabled}
          onChange={toggleMusic}
          className="music-checkbox"
        />
      </label>

      {/* Activate Audio - When browser blocks autoplay */}
      {state.needsUserActivation && state.enabled && (
        <>
          <div className="menu-divider"></div>
          <button className="menu-item activate-audio" onClick={activateAudio}>
            <span className="menu-icon"><Play size={18} weight="duotone" /></span>
            <span className="menu-text">Click to Enable Audio</span>
          </button>
        </>
      )}

      {/* Playback Controls - Only when audio is active */}
      {isAudioActive && (
        <>
          <div className="menu-divider"></div>
          <div className="menu-section-header">Playback</div>
          <div className="playback-controls">
            <button className="playback-button" onClick={handlePauseResume}>
              <span className="menu-icon">{state.status === 'playing' ? <Pause size={18} weight="duotone" /> : <Play size={18} weight="duotone" />}</span>
              <span>{state.status === 'playing' ? 'Pause' : 'Resume'}</span>
            </button>
            <button className="playback-button" onClick={replaySection}>
              <span className="menu-icon"><ArrowCounterClockwise size={18} weight="duotone" /></span>
              <span>Replay</span>
            </button>
          </div>
        </>
      )}

      {/* Speed Control - Only when narration is enabled */}
      {state.enabled && (
        <>
          <div className="menu-divider"></div>
          <div className="menu-section-header">Speed</div>
          <div className="speed-control">
            {SPEED_PRESETS.map((speed) => (
              <button
                key={speed}
                className={`speed-button ${state.playbackRate === speed ? 'active' : ''}`}
                onClick={() => setPlaybackRate(speed)}
              >
                {speed}x
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NarrationMenu;